/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useCallback } from 'react';
import { useSpring } from 'react-spring';
import { EditIcon, TrashIcon } from 'src/components/Icons';
import { isNullOrEmptyArray } from '../../../util/arrays';
import { Loading } from '../../Loading';
import { useEventBus } from 'src/hooks';

import * as Ui from './styles';
import { ON_CLEAR_SELECTIONS } from 'src/hooks/EventBus/eventTypes';

const SUBITEM_MIN_HEIGHT = 28;
const CONSOLIDATION_TYPE = 'consolidation';

/**
 * Componente que possibilita criar uma listagem de itens selecionáveis, bem como a si mesmo.
 * Exemplo de uso.
 *
 * <CollapsibleList
 *   title="Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"
 *   checkedTitle={true}
 *   boldedTitle={false}
 *   multCheck={true}
 *   collapsed={false}
 *   data={[
 *     {value: 1, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 2, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 3, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 4, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 5, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 6, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *     {value: 7, label: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. In maximus"},
 *   ]}
 *   selectedValues={[2, 7]}
 * />
 * @param {*} param0
 */
function ConsolidationCollapsibleListComp({
  id,
  index,
  // título da lista
  title,
  // define se o componente é expansível, caso não seja o comportamento de clique será igual ao de seleção
  expansible = true,
  // define se o título está recolhido
  collapsed = false,
  // define se o título está selecionado
  checkedTitle = false,
  // define se o título tem destaque
  boldedTitle = false,
  // define um tooltip para o título
  tooltipTitle,
  // define se o componente possui comportamento de multiseleção
  multCheck = false,
  // define se os itens podem ser checados
  checkable = true,
  // define se apresenta um loading ao invés de dados quando o componente estiver expandido
  loading = false,
  // destaque o item em foco
  highlightFocusItem = false,
  // dados a serem apresentados.Ex.: [{value: 1, label: "Lorem ipsum dolor"}]
  data = [],
  // valores selecionados [1, 2, ...]
  selectedValues = [],
  // evento de propagação de seleção do título
  onSelectTitle,
  // evento de propagação da ação de expandir
  onExpand,
  // evento de propagação da ação de retrair
  onCollapse,
  // evento de propagação de alteração da listagem de seleção
  onChangeSelectedItems,
  // define se a seta ira aparecer
  withArrow = true,
  // define se o item sera exibido em letras maiusculas
  isUpperCase = true,
  // define o padding do texto
  textPadding,
  // define o formato das opções
  checkboxFormat = false,
  // consolidações definidas
  consolidations,
  // evento de propagação da ação de editar uma consolidação
  handleEditModeChanges,
  // evento de propagação da ação de deletar uma consolidação
  onDeleteConsolidation,
}) {
  // states
  const [initialData, setInitialData] = useState([]);
  const [initialSelectedValues, setInitialSelectedValues] = useState([]);
  const [internalSelectedValues, setInternalSelectedValues] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsSet, setItemsSet] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [bodyVisibility, setBodyVisibility] = useState(!collapsed);
  const dataGroupQt = loading ? 3 : Math.round(data.length / 3) + 1;

  // Event Bus
  const eventBus = useEventBus();

  // callbacks
  const setSelectedItemsProxy = useCallback((items) => {
    const values = items.map((i) => i.value);
    setSelectedItems(items);
    setInternalSelectedValues(values);
  }, []);

  useEffect(() => {
    eventBus.subscribe(ON_CLEAR_SELECTIONS, () => {
      // atribui estes itens no estado do componente
      setSelectedItemsProxy([]);
      // propaga o evento de seleção
      if (onChangeSelectedItems) onChangeSelectedItems([]);
    });
  }, []);

  // effect of change list data
  useEffect(() => {
    if (!isNullOrEmptyArray(data)) {
      setInitialData(data);
    }
  }, [data]);

  // effect of change selected values
  useEffect(() => {
    if (!isNullOrEmptyArray(selectedValues)) {
      setInitialSelectedValues(selectedValues);
    }
  }, [selectedValues]);

  // efeito de definir os itens selecionados
  useEffect(() => {
    if (isNullOrEmptyArray(initialData)) return;
    // cria um vetor de itens selecionados
    const sel = initialData.filter((item) => initialSelectedValues.includes(item.value));
    // atribui estes itens no estado do componente
    setSelectedItemsProxy(sel);
  }, [initialData, initialSelectedValues, setSelectedItemsProxy]);

  // efeito de definir os itens selecionados
  useEffect(() => {
    const withConsolidations = !isNullOrEmptyArray(consolidations);
    const editModeConsolidation = withConsolidations
      && consolidations.find((consolidation) => consolidation.editMode);

    if (isNullOrEmptyArray(initialData)) return;
    // checker
    function wasSelected(value) {
      return internalSelectedValues.includes(value);
    }

    // cria um vetor de itens selecionados
    const items = initialData.map((item) => ({ ...item, checked: wasSelected(item.value) })); //.filter(filterConsolidations);

    let filterConsolidations = null;

    // lógica para transformar o array de items em um array de items e items de consolidação, apenas deve ser considerada
    // se não estivermos no modo de edição de uma consolidação
    if (!editModeConsolidation) {
      const tempItems = [...items];

      // Percorremos as consolidações
      consolidations.forEach((consolidation) => {
        const alts = consolidation.alternatives;

        // Pegamos as alternativas consolidadas a partir do índice 1, iteramos e removemos essas
        // alternativas do array de items consumido pela listagem
        alts.slice(1).forEach((alt) => {
          const findedItemIndex = items.findIndex(({ value }) => value === alt.value)
          findedItemIndex >= 0 && items.splice(findedItemIndex, 1);
        });
      });

      // Aqui, criamos, de fato, a consolidação e setamos no array de items (itemsSet)
      consolidations.forEach((consolidation, consolidationIndex) => {
        const alts = consolidation.alternatives;
        const firstIndexOfConsolidatedItem = items.findIndex((item) => item.value === alts[0].value);

        items[firstIndexOfConsolidatedItem] = {
          // Caso a consolidação tenha um id definido irá considerá-lo
          ...(consolidation.value ? { value: consolidation.value } : {}),
          label: consolidation.title,
          consolidates: tempItems
            .filter((alt) => alts.some((a) => a.value === alt.value))
            .map((alt) => ({ ...alt, checked: false })),
          refIndex: consolidationIndex,
        };
      });
    }
    else {
      // Função de filtro que persiste apenas os itens da consolidação atualmente em edição
      filterConsolidations = function filterConsolidationsInEditMode(item) {
        const inCurrentConsolidation = editModeConsolidation.alternatives
          .some((citem) => citem.value === item.value);
        const noConsolidation = !consolidations.some(
          (consolidation) => consolidation.alternatives.find(citem => citem.value === item.value)
        );
        return inCurrentConsolidation || noConsolidation;
      }
    }

    const finalItems = filterConsolidations
      ? items.filter(filterConsolidations)
      : items;
    // atribui estes itens no estado do componente
    setItemsSet(finalItems);
  }, [initialData, internalSelectedValues, consolidations]);

  // collapse or expand
  const collapseAnimation = useSpring({
    config: { duration: 100 },
    from: {
      height: isCollapsed ? `${dataGroupQt * SUBITEM_MIN_HEIGHT + 5}px` : '0px',
    },
    to: {
      height: isCollapsed ? '0px' : `${dataGroupQt * SUBITEM_MIN_HEIGHT + 5}px`,
    },
    onStart: () => {
      if (!isCollapsed) setBodyVisibility(true);
    },
    onRest: () => {
      if (isCollapsed) setBodyVisibility(false);
    },
  }, [isCollapsed]);

  //
  function toogleTitle() {
    if (boldedTitle) return;
    const selected = !checkedTitle;
    if (onSelectTitle) onSelectTitle(selected, index);
  }

  //
  function checkItem(item) {
    // recria o vetor de itens selecionas e adiciona item
    const { value, label } = item;
    const sel = [...selectedItems, { value, label }];
    // atribui estes itens no estado do componente
    setSelectedItemsProxy(sel);
    // propaga o evento de seleção
    if (onChangeSelectedItems) onChangeSelectedItems(sel);
    // ao selecionar item em caso de não estar selecionado
    if (!checkedTitle) {
      toogleTitle();
    }
  }

  function uncheckItem(item) {
    // recria o vetor de itens selecionas e adiciona item
    const sel = selectedItems.filter((si) => si.value !== item.value);
    // atribui estes itens no estado do componente
    setSelectedItemsProxy(sel);
    // propaga o evento de seleção
    if (onChangeSelectedItems) onChangeSelectedItems(sel);
    // remove a seleção caso nenhum item esteja selecionado
    if (checkedTitle && isNullOrEmptyArray(internalSelectedValues)) {
      toogleTitle();
    }
  }

  function checkOrUncheckItem(item) {
    // verifica se já está selecionado
    if (internalSelectedValues.includes(item.value)) {
      uncheckItem(item);
    } else {
      checkItem(item);
    }
  }

  function clearAndCheckItem(item) {
    // define o novo vetor de selecionados
    const { value, label } = item;
    const sel = [{ value, label }];
    // ignora os itens existentes e adiciona o último selecionado
    setSelectedItemsProxy(sel);
    // propaga o evento de seleção
    if (onChangeSelectedItems) onChangeSelectedItems(sel);
    // ao selecionar qualq
    if (!checkedTitle) {
      toogleTitle();
    }
  }

  function handleSelectItem(item) {
    // verifica se é possível selecinar itens
    if (!checkable) return;
    // verifica qual o tipo de seleção (mult ou single)
    if (multCheck) {
      checkOrUncheckItem(item);
    } else {
      clearAndCheckItem(item);
    }
  }

  function handleSelectTitle() {
    if (checkedTitle) {
      // remove todas as seleções
      setSelectedItemsProxy([]);
      // propaga o evento de seleção vazia
      if (onChangeSelectedItems) onChangeSelectedItems([]);
    }
    //
    toogleTitle();
  }

  /**
   * A manipulação do toque no título será
   * igual ao de selecionar o título se o componente for não expansível.
   * Caso contrário, ele irá verificar se há itens e expandir para mostrar
   */
  function handleCollapse() {
    if (!expansible) handleSelectTitle();
    else {
      setIsCollapsed(!isCollapsed);
      if (isCollapsed) {
        // {label: string : checked: boolean, value:'algo de valor unico'}
        if (onExpand) onExpand({ label: title, checkedTitle, value: id }, index);
      } else if (onCollapse) onCollapse();
    }
  }

  /**
   * callbacks: Disparado ao editar uma consolidação, o método irá recuperar o estado anterior das alternativas
   * e setá-lo.
   */
  const handleOnEditMode = useCallback((consolidationIndex) => {
    const findedConsolidationIndex = itemsSet.findIndex((item) => item.refIndex === consolidationIndex);

    if (findedConsolidationIndex !== -1) {
      // Recuperamos os items originais consolidados
      const { consolidates } = itemsSet[findedConsolidationIndex];
      // Lógica de realocação no array de items
      const slicedRight = itemsSet.slice(findedConsolidationIndex + 1, itemsSet.length);
      const slicedLeft = itemsSet.slice(0, findedConsolidationIndex);

      // Items sem consolidações
      const newItemsSet = [...slicedLeft, ...consolidates, ...slicedRight].filter((item) => {
        return !item.consolidates;
      });

      setItemsSet(newItemsSet);
    }

    if (handleEditModeChanges) handleEditModeChanges(consolidationIndex);
  }, [consolidations, itemsSet]);

  /**
   * callbacks: Disparado ao deletar uma consolidação
   */
  const handleOnDeleteConsolidation = useCallback((consolidationIndex) => {
    if (onDeleteConsolidation && consolidationIndex !== -1) onDeleteConsolidation(consolidationIndex);
  }, [consolidations]);

  // renders: loading
  function renderLoadingSpinner() {
    return (
      <Ui.ItemsEmpty>
        <Loading size={30} running={loading} />
      </Ui.ItemsEmpty>
    );
  }

  const ItemWrapper = (wrappedProps) => (checkable ? (
    <Ui.Item {...wrappedProps} />
  ) : (
    <Ui.FixedItem {...wrappedProps} />)
  );

  const ConsolidationItemWrapper = (wrappedProps) => (
    <Ui.ConsolidationWrapper {...wrappedProps} />
  )

  return (
    <Ui.Container
      isChecked={checkedTitle}
      isCollapsed={isCollapsed}
      title={tooltipTitle}
      className="CollapsibleList"
      highlightFocusItem={highlightFocusItem}
    >
      <Ui.Head textPadding={textPadding}>
        <Ui.CircleWrapper clear isBolded={boldedTitle} onClick={() => handleSelectTitle()}>
          {
            !checkboxFormat ? (
              <Ui.Circle size={15} isBolded={boldedTitle} isChecked={checkedTitle} />
            ) : <Ui.Checkbox size={15} isBolded={boldedTitle} isChecked={checkedTitle}></Ui.Checkbox>
          }
        </Ui.CircleWrapper>
        <Ui.Collapser clear onClick={() => handleCollapse()}>
          {
           withArrow && <Ui.Arrow size={18} color={'#610082'} isChecked={checkedTitle} />
          }
          {/* TODO: (victor, resolver) Removido pois estava comprometendo a performance e fugindo do design do resto dos tooltips */}
          {/* {tooltipTitle ? (
            <Ui.TextWithHint isBolded={boldedTitle} tooltip={tooltipTitle}>
              {title}
            </Ui.TextWithHint>
          ) : (
            <Ui.Title isBolded={boldedTitle} title={tooltipTitle}>{title}</Ui.Title>
          )} */}
          <Ui.Title isBolded={boldedTitle} isUpperCase={isUpperCase}>{title}</Ui.Title>
        </Ui.Collapser>
      </Ui.Head>
      <Ui.Body style={{ ...collapseAnimation, display: bodyVisibility ? 'flex' : 'none' }}>
        {loading && !isCollapsed ? renderLoadingSpinner() : (
          <Ui.Items isOpen={!isCollapsed}>
            {itemsSet.map((item) => {
              return !item.consolidates
                  ? (
                    <ItemWrapper key={`id-${item.value}`} clear onClick={() => handleSelectItem(item)}>
                      {
                        !checkboxFormat ? (
                          <Ui.CircleItem size={15} isChecked={item.checked} isBolded={boldedTitle} />
                        ) : <Ui.Checkbox size={15} isChecked={item.checked} />
                      }
                      <Ui.Text>{item.label}</Ui.Text>
                    </ItemWrapper>
                  )
                  : (
                    <ConsolidationItemWrapper key={`id-${item.value || item.consolidates.map((i) => i.value).join('.')}`} clear>
                      {item.label}
                      <Ui.ConsolidationActionGroup>
                        <EditIcon size={12.55} onClick={() => handleOnEditMode(item.refIndex)} />
                        <TrashIcon size={12.55} onClick={() => handleOnDeleteConsolidation(item.refIndex)} />
                      </Ui.ConsolidationActionGroup>
                    </ConsolidationItemWrapper>
                  )
            })}
          </Ui.Items>
        )}
      </Ui.Body>
    </Ui.Container>
  );
}

export const ConsolidationCollapsibleList = React.memo(ConsolidationCollapsibleListComp);
